import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

interface SheetTabBarProps {
  names: string[]
  active: number
  onSelect: (index: number) => void
}

/** A workbook's sheet tabs below the sheet, as in Excel (cdb_webos#1465); they sit in the preview's bar. */
export function SheetTabBar({ names, active, onSelect }: SheetTabBarProps) {
  const { t } = useTranslation()
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])

  // Many sheets scroll sideways; the open one stays in view.
  useEffect(() => {
    tabRefs.current[active]?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  }, [active])

  return (
    <div
      role="tablist"
      aria-label={t('pdfViewer.sheetTabs')}
      className="flex min-w-0 flex-1 items-stretch gap-0.5 overflow-x-auto"
    >
      {names.map((name, index) => {
        const selected = index === active
        return (
          <button
            key={`${index}-${name}`}
            ref={(el) => { tabRefs.current[index] = el }}
            type="button"
            role="tab"
            aria-selected={selected}
            title={name}
            onClick={() => onSelect(index)}
            className={`max-w-[16rem] shrink-0 truncate border-b-2 px-3 py-1.5 text-xs transition-colors ${
              selected
                ? 'border-[var(--theme-interactive-color)] font-semibold text-[var(--text-color)]'
                : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-color)]'
            }`}
          >
            {name}
          </button>
        )
      })}
    </div>
  )
}
